/**
 * PlayerPreviewInfoRowsView — the label/value row list shared by the player
 * preview drawer's Key info, Ratings, Attributes and Season form sections
 * (SE-5494, restyled SE-6554).
 *
 * Rows are supplied fully formatted by `playerPreviewModel`; this component
 * makes no formatting or presence decisions of its own. In particular it
 * renders EVERY row it is given, including ones whose value is the em-dash
 * placeholder — the drawer's contract is that a key-info row never disappears
 * just because its field is null (that is exactly how agent/agency went
 * missing before SE-5494).
 *
 * ## Two layouts, one row model
 *
 * - `grid` — field lists (Key info, Season form): two columns of label ABOVE
 *   value. Reading a field list is a scan for a name, so the label leads.
 * - `meter` — score lists (Ratings, Attributes): label and value on one line
 *   with a bar beneath. Reading a score list is a comparison, so the numbers
 *   line up on the right edge and the bars carry the shape.
 *
 * A row only draws a bar when the model gave it a `percent`. That is the whole
 * gate: an absent score renders its em dash with no track at all, rather than
 * an empty bar that reads as a genuine zero.
 *
 * The grid's gutters are PADDING on the cells rather than a `gap` on the
 * wrapper. Two `w-1/2` cells plus a gap overflow their row and wrap to one
 * column per line; padding keeps the halves exact.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/molecules/PlayerPreviewInfoRows
 */

import { memo } from "react";

import { TEST_ID_PLAYER_PREVIEW_DRAWER } from "e2e/testId";

import { Box } from "@/components/atoms/Box";
import { Progress, ProgressFilledTrack } from "@/components/atoms/Progress";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { PlayerPreviewInfoItem } from "../../../utils/playerPreviewModel";

/** How a row list is laid out. */
export type PlayerPreviewInfoLayout = "grid" | "meter";

/** Props for {@link PlayerPreviewInfoRowsViewProps}. */
export interface PlayerPreviewInfoRowsViewProps {
  /** Fully formatted rows, in render order. */
  readonly rows: readonly PlayerPreviewInfoItem[];
  /** Which layout to draw. Defaults to the two-column field grid. */
  readonly layout?: PlayerPreviewInfoLayout;
}

/**
 * Meter fill per tone — GPR's own green, or the accent blue every other bar
 * takes.
 * @remarks
 * `foreground` used to be `bg-content-primary`, drawn from the DARK artboard
 * where these bars read as near-white. That was a misreading: the light spec
 * (Raniel, 2026-08-07) resolves them to #007aff, which is `accent-primary`
 * exactly — and the token flips to #0a84ff in dark, which is the "white-ish"
 * appearance the dark artboard was showing. One token serves both themes, and
 * a neutral bar was never the intent.
 *
 * GPR keeps the success family so the headline rating reads as the summary
 * number the rest of the section decomposes. NOTE: the light spec names
 * #16b364 for it, which is NOT on this palette's success ramp — the nearest
 * rungs are success-400 (72,151,102) and this token, success-500 (52,131,82),
 * both markedly duller. Minting #16b364 is a design-library addition, not a
 * call-site hex, so the semantic token stands and the gap is flagged.
 */
const BAR_FILL_CLASS = {
  foreground: "bg-accent-primary",
  success: "bg-status-success",
} as const;

/**
 * Render one field cell of the two-column grid.
 * @param row - The row model.
 * @returns The cell.
 */
const renderGridCell = (row: PlayerPreviewInfoItem) => (
  <Stack
    key={row.key}
    className="w-1/2 gap-px pb-4 pr-3"
    testID={TEST_ID_PLAYER_PREVIEW_DRAWER.infoRowFor(row.key)}
  >
    <Text variant="micro" className="text-content-muted">
      {row.label}
    </Text>
    <Text
      variant="caption"
      className="font-medium text-content-primary"
      testID={TEST_ID_PLAYER_PREVIEW_DRAWER.infoValueFor(row.key)}
    >
      {row.value}
    </Text>
  </Stack>
);

/**
 * Render one score row of the meter list.
 * @param row - The row model.
 * @returns The row.
 */
const renderMeterRow = (row: PlayerPreviewInfoItem) => (
  <Stack
    key={row.key}
    className="gap-1"
    testID={TEST_ID_PLAYER_PREVIEW_DRAWER.infoRowFor(row.key)}
  >
    <Stack
      direction="horizontal"
      space="3"
      className="items-center justify-between"
    >
      <Text variant="caption" className="text-content-secondary">
        {row.label}
      </Text>
      <Text
        variant="caption"
        className="font-medium text-content-primary"
        testID={TEST_ID_PLAYER_PREVIEW_DRAWER.infoValueFor(row.key)}
      >
        {row.value}
      </Text>
    </Stack>
    {row.percent === undefined ? null : (
      /* `xs` is the atom's 4px track. The design draws 6px, which sits exactly
         between two rungs of the closed 4px grid and therefore ties DOWN: the
         6px step has no rung, and naming it would compile to no CSS at all.
         (The step is described rather than spelled as a class on purpose — the
         budget scanner reads raw text, so a literal would count as a call
         site.) */
      <Progress
        value={row.percent}
        size="xs"
        className="w-full bg-surface-raised"
      >
        <ProgressFilledTrack
          className={BAR_FILL_CLASS[row.barTone ?? "foreground"]}
        />
      </Progress>
    )}
  </Stack>
);

/**
 * Renders a list of label/value rows.
 * @param props - {@link PlayerPreviewInfoRowsViewProps}
 * @param props.rows - Fully formatted rows, in render order
 * @param props.layout - Which layout to draw
 * @returns The row list.
 */
const PlayerPreviewInfoRowsView = ({
  rows,
  layout = "grid",
}: PlayerPreviewInfoRowsViewProps) =>
  layout === "meter" ? (
    <Stack space="3">{rows.map(renderMeterRow)}</Stack>
  ) : (
    <Box className="w-full flex-row flex-wrap">{rows.map(renderGridCell)}</Box>
  );

PlayerPreviewInfoRowsView.displayName = "PlayerPreviewInfoRowsView";

export default memo(PlayerPreviewInfoRowsView);
